import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import RedirectHandler from './components/RedirectHandler';
import LoadingFallback from './components/LoadingFallback';
import { CampaignProvider } from './contexts/CampaignContext';

import './App.css';

const HomePage = lazy(() => import('./pages/home/Home'));
const ActiveYearCampaignPage = lazy(() => import('./pages/campaign/ActiveYearCampaign'));
const SchedulePage = lazy(() => import('./pages/campaign/Schedule'));
const MatchingPage = lazy(() => import('./pages/campaign/Matching'));
const RaisedFundsPage = lazy(() => import('./pages/campaign/RaisedFunds'));
const SubmitPage = lazy(() => import('./pages/campaign/Submit'));
const PreviousYearPage = lazy(() => import('./pages/previousyear/PreviousYear'));
const Page = lazy(() => import('./pages/page/Page'));
const NotFound = lazy(() => import('./pages/notfound/NotFound'));
const Perks = lazy(() => import('./pages/campaign/Perks'));
const FAQ = lazy(() => import('./pages/faq/FAQ'));
const VideoOverview = lazy(() => import('./pages/videos/VideoOverview'));
const VideoDetail = lazy(() => import('./pages/videos/VideoDetail'));
const FeaturedVideoPage = lazy(() => import('./pages/videos/FeaturedVideoPage'));
const WhoWeArePage = lazy(() => import('./pages/whoweare/WhoWeAre'));
const HowItWorksPage = lazy(() => import('./pages/howitworks/HowItWorks'));
const Preview = lazy(() => import('./pages/preview/Preview'));
const OverlayP4APage = lazy(() => import('./pages/overlay/OverlayP4A'));
const DonorsOverlayPage = lazy(() => import('./pages/overlay/DonorsOverlay'));
const LiveDonationsOverlayPage = lazy(() => import('./pages/overlay/LiveDonationsOverlay'));
const JarrodPage = lazy(() => import('./pages/jarrod/Jarrod'));
const DowntimePage = lazy(() => import('./pages/downtime/Downtime'));

function App() {
  // Simpler campaign routes
  const activeCampaignPath = `/campaign`;
  const recapCampaignPath = `/recap`;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/overlay/p4a" element={<OverlayP4APage />} />
        <Route path="/overlay/live" element={<LiveDonationsOverlayPage />} />
        <Route path="/donors" element={<DonorsOverlayPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<CampaignProvider><HomePage /></CampaignProvider>} />
          {/* Dedicated campaign routes (flattened paths) */}
          <Route path="/schedule" element={<ActiveYearCampaignPage />}>
            <Route index element={<SchedulePage />} />
          </Route>
          <Route path="/matching" element={<ActiveYearCampaignPage />}>
            <Route index element={<MatchingPage />} />
          </Route>
          <Route path="/raised" element={<ActiveYearCampaignPage />}>
            <Route index element={<RaisedFundsPage />} />
          </Route>
          <Route path="/nominate-a-charity" element={<ActiveYearCampaignPage />}>
            <Route index element={<SubmitPage />} />
          </Route>
          <Route path="/submit" element={<Navigate to="/nominate-a-charity" replace />} />
          <Route path="/charities" element={<Navigate to="/nominate-a-charity" replace />} />
          <Route path="/perks" element={<ActiveYearCampaignPage />}>
            <Route index element={<Perks />} />
          </Route>

          {/* Backwards-compatible redirects from old campaign paths */}
          <Route path={activeCampaignPath} element={<Navigate to="/schedule" replace />} />
          <Route path="/campaign-:year" element={<Navigate to="/schedule" replace />} />
          <Route path={recapCampaignPath} element={<PreviousYearPage />} />
          <Route path="/who-we-are" element={<WhoWeArePage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/api/preview" element={<Preview />} />
          <Route path="/:slug" element={<Page />} />
          <Route path="/submissions" element={<Navigate to="/campaign/submissions" replace />} />
          <Route path="/videos" element={<VideoOverview />} />
          <Route path="/featured" element={<CampaignProvider><FeaturedVideoPage /></CampaignProvider>} />
          <Route path="/videos/:slug" element={<CampaignProvider><VideoDetail /></CampaignProvider>} />
          <Route path="/faq" element={<FAQ />} />
          {/* Easter egg page */}
          <Route path="/jarrod" element={<JarrodPage />} />
          {/* Unlisted — no nav entry */}
          <Route path="/downtime" element={<DowntimePage />} />
          {/* Dynamic redirect handler - checks Strapi for redirects before showing 404 */}
          <Route path="*" element={<RedirectHandler notFoundComponent={<NotFound />} />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
